import { useCallback, useMemo } from 'react';
import { CALYPSO } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import * as OfferingType from 'marketplace-ui-common/offerings/constants/OfferingType';
import { toSearchQuery } from '../../common/utils/assetQuery';
import { ModuleQuery } from '../records/ModuleQuery';
import AssetResultsSectionDataContainer from '../../storefront/components/AssetResultsSectionDataContainer';
import RouterFilterButton from '../../common/components/RouterFilterButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_RECOMMENDATION_COUNT = 3;
export default function ModuleRecommendations({
  categories,
  categoryTranslations,
  isPreview,
  offeringId,
  onTrackInteraction,
  onTrackPageview,
  providerName
}) {
  const {
    textAlign
  } = useResponsiveProps({
    textAlign: {
      xs: 'left'
    }
  });
  const providerQuery = useMemo(() => new ModuleQuery({
    providers: [providerName],
    limit: MAX_RECOMMENDATION_COUNT + 1
  }), [providerName]);
  const categoryQuery = useMemo(() => new ModuleQuery({
    categories,
    limit: MAX_RECOMMENDATION_COUNT + 1
  }), [categories]);
  const categoriesSeeAllMessage = useMemo(() => {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.modules.details.recommendations.seeAllCategories",
      options: {
        categoryTypes: I18n.formatList(categoryTranslations)
      }
    });
  }, [categoryTranslations]);
  const categoriesTitle = useMemo(() => {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.modules.details.recommendations.categoriesTitle",
      options: {
        categoryTypes: I18n.formatList(categoryTranslations)
      }
    });
  }, [categoryTranslations]);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'recommendations'
    }));
  }, [onTrackInteraction]);
  const trackPageview = useCallback(eventProperties => {
    onTrackPageview(eventProperties);
  }, [onTrackPageview]);
  const renderRecommendationRouter = useCallback((message, moduleQueryField, queryValue) => /*#__PURE__*/_jsxs(RouterFilterButton, {
    disabled: isPreview,
    onClick: () => trackInteraction('click see all link'),
    queryField: moduleQueryField,
    style: {
      textAlign
    },
    value: queryValue,
    use: "transparent",
    children: [message, /*#__PURE__*/_jsx(UIIcon, {
      name: "right",
      size: "xxs",
      color: CALYPSO
    })]
  }), [isPreview, textAlign, trackInteraction]);
  return /*#__PURE__*/_jsx(MainSection, {
    responsive: true,
    children: /*#__PURE__*/_jsxs(MainSectionInner, {
      children: [/*#__PURE__*/_jsx(AssetResultsSectionDataContainer, {
        maxColumns: MAX_RECOMMENDATION_COUNT,
        offeringId: offeringId,
        responsive: true,
        searchQuery: toSearchQuery(providerQuery, OfferingType.MODULE),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.modules.details.recommendations.providerTitle",
          options: {
            providerName
          }
        }),
        titleControls: renderRecommendationRouter( /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.modules.details.recommendations.seeAllProviders",
          options: {
            providerName
          }
        }), 'providers', providerName),
        trackInteraction: trackInteraction,
        trackPageview: trackPageview
      }), /*#__PURE__*/_jsx(AssetResultsSectionDataContainer, {
        maxColumns: MAX_RECOMMENDATION_COUNT,
        offeringId: offeringId,
        responsive: true,
        searchQuery: toSearchQuery(categoryQuery, OfferingType.MODULE),
        title: categoriesTitle,
        titleControls: renderRecommendationRouter(categoriesSeeAllMessage, 'categories', categories),
        trackInteraction: trackInteraction,
        trackPageview: trackPageview
      })]
    })
  });
}